<template>
  <iColumn
    v-click-outside="handleClickOutside"
    gap="extraSmall"
    width="hug"
    vertical-align="middle"
    align="left"
  >
    <iRow
      gap="small"
      vertical-align="middle"
      width="hug"
    >
      <iRow
        gap="small"
        vertical-align="middle"
        align="left"
        width="hug"
      >
        <template v-if="showFullHashtags">
          <iRow
            v-for="item in modelValue"
            :key="getHashTagValue(item)"
            width="hug"
            vertical-align="middle"
          >
            <iText
              v-if="!edit"
              font-size="small"
            >
              #{{ getHashTagValue(item) }}
            </iText>
            <iChip
              v-else
              show-icon
              icon-position="right"
              show-border-on-hover
              show-close-on-hover
              size="small"
              variant="subtle"
              :icon-hover-opacity="1"
              :style-overrides="{
                borderRadius: '50px',
                borderColor: 'none',
                subtleBackgroundColor: 'light',
                subtleHoverBackgroundColor: 'dark',
                smallPaddingTop: 'none',
                smallPaddingBottom: 'none',
                smallPaddingLeft: 'small',
                smallPaddingRight: 'none',
                iconBorderRadius: '50px',
                iconBackgroundColor: 'light',
                iconHoverBackgroundColor: 'light',
                iconSize: '14px',
                iconPaddingTop: 'extraSmall',
                iconPaddingBottom: 'extraSmall',
                iconPaddingLeft: 'extraSmall',
                iconPaddingRight: 'extraSmall',
                subtleIconColor: 'standard',
              }"
              @mouseenter="hoveredTagValue = getHashTagValue(item)"
              @mouseleave="hoveredTagValue = null"
              @click:icon="removeHashtag(getHashTagValue(item))"
            >
              <iText
                variant="standard"
                :style-overrides="{
                  standardFontColor: getTagFontColor(getHashTagValue(item)),
                  standardHoverFontColor: getTagFontColor(getHashTagValue(item)),
                }"
              >
                #{{ getHashTagValue(item) }}
              </iText>
            </iChip>
          </iRow>
        </template>

        <template v-if="canAddHashTags">
          <iRow
            class="parent-input-box"
            align="left"
            width="hug"
          >
            <iButton
              v-if="edit && modelValue.length < 4"
              class="add-hashtag-btn"
              variant="tertiary"
              icon-right="ic:baseline-plus"
              size="extraSmall"
              icon-right-size="small"
              icon-right-variant="subtle"
              :icon-color="getIconColor"
              :style-overrides="{
                borderRadius: '50px',
                extraSmallPaddingTop: 'extraSmall',
                extraSmallPaddingBottom: 'extraSmall',
                extraSmallPaddingLeft: 'extraSmall',
                extraSmallPaddingRight: 'extraSmall'
              }"
              @mouseenter="addTagHovered = true"
              @mouseleave="addTagHovered = false"
              @click="enableAddHashtag"
            />
            <iRow
              v-if="showAddHashtag"
              class="floating-input-box"
              wrap="nowrap"
              align="left"
              vertical-align="middle"
              gap="small"
              width="hug"
              :style-overrides="{
                backgroundColor: 'light',
                borderRadius: '50px',
                paddingTop: 'extraSmall',
                paddingBottom: 'extraSmall',
                paddingLeft: 'small',
                paddingRight: 'small',
              }"
            >
              <iTextInput
                ref="hashtagTextEdit"
                v-model="hashtag"
                size="small"
                width="hug"
                placeholder="Type Hashtag"
                :style-overrides="{
                  inputBorderWidth: 'none',
                  inputBorderColor: 'none',
                  inputBackgroundColor: 'light',
                  inputBorderFocusWidth: 'none',
                  inputHoverBackgroundColor:'light',
                  inputBorderFocusRadius: '50px',
                  inputBorderRadius: '50px',
                }"
                @update:model-value="handleHashtagInput"
                @keydown="handleKeydown"
              />
              <iButton
                class="add-hashtag-btn"
                variant="tertiary"
                size="extraSmall"
                :style-overrides="{
                  borderRadius: '50px',
                  tertiaryVariantHoverBackgroundColor: 'dark',
                  tertiaryVariantFontColor: getSaveButtonFontColor,
                  tertiaryVariantHoverFontColor: getSaveButtonFontColor,
                }"
                @mouseenter="saveButtonHovered = true"
                @mouseleave="saveButtonHovered = false"
                @click.stop="saveTag()"
              >
                Save
              </iButton>
              <iButton
                class="add-hashtag-btn"
                variant="secondary"
                size="extraSmall"
                icon-left="material-symbols:close"
                icon-left-size="small"
                :icon-color="getCloseButtonIconColor"
                :style-overrides="{
                  borderRadius: '50px',
                  secondaryVariantBorderColor: 'none',
                  secondaryVariantHoverBorderColor: 'none',
                  extraSmallPaddingLeft: 'extraSmall',
                  extraSmallPaddingRight: 'extraSmall',
                  secondaryVariantHoverBackgroundColor: 'dark',
                }"
                @mouseenter="closeButtonHovered = true"
                @mouseleave="closeButtonHovered = false"
                @click.stop="closeAddTag"
              />
            </iRow>
          </iRow>
        </template>
      </iRow>
      <iText
        v-if="validationMessage"
        variant="error"
      >
        {{ validationMessage }}
      </iText>
    </iRow>
  </iColumn>
</template>

<script>
const DEFAULT_MAX_HASHTAGS = 8;

export default {
  name: "StoryHashtagInput",
  props: {
    modelValue: {
      type: Array,
      required: true,
      default: () => [],
    },
    hashtagValueKey: {
      type: String,
      default: "value",
      required: false,
    },
    splitHashTagsBy: {
      type: Array,
      default: () => [" ", ","],
      required: false,
    },
    placeholder: {
      type: String,
      default: "Add a hashtag",
      required: false,
    },
    allowDuplicates: {
      type: Boolean,
      default: false,
      required: false,
    },
    edit: {
      type: Boolean,
      default: true,
      required: false,
    },
    validation: {
      type: Function,
      required: false,
      default: () => true,
    },
    // How many hashtags the destination allows; null keeps the editor's own cap.
    maxCount: {
      type: Number,
      required: false,
      default: null,
    },
  },
  emits: ["update:modelValue", "submit:hashtag", "delete:hashtag", "close:hashtag"],
  data() {
    return {
      hashtag: "",
      showAddHashtag: false,
      validationMessage: "",
      hashtagRowInFocus: false,
      keyboardKeysToContinueEditAfterSubmit: ["Tab", " "],
      keyboardKeysToSubmit: ["Tab", " ", "Enter"],
      showFullHashtags: true,
      addTagHovered: false,
      hoveredTagValue: null,
      saveButtonHovered: false,
      closeButtonHovered: false,
    };
  },
  computed: {
    canAddHashTags() {
      const cap = this.maxCount === null ? DEFAULT_MAX_HASHTAGS : this.maxCount;
      return this.modelValue.length < cap;
    },
    getIconColor() {
      return this.addTagHovered ? "light" : "standard";
    },
    getButtonFontColor() {
      return this.addTagHovered ? "light" : "standard";
    },
    getSaveButtonFontColor() {
      return this.saveButtonHovered ? "light" : "standard";
    },
    getCloseButtonIconColor() {
      return this.closeButtonHovered ? "light" : "standard";
    },
  },
  watch: {
    hashtag(newValue) {
      this.runValidation(this.formatHashtag(newValue));
    },
    showAddHashtag(newValue) {
      if (!newValue) {
        this.$emit("close:hashtag");
        this.clearHashtag();
      } else {
        this.$nextTick(() => {
          if (this.$refs.hashtagTextEdit) {
            this.$refs.hashtagTextEdit.setFocus();
          }
        });

      }
    },
  },
  methods: {
    enableAddHashtag() {
      this.showAddHashtag = true;
    },
    updateHashtag(value) {
      if (this.validationMessage) {
        return;
      }
      if (!value) {
        return;
      }

      const formattedValue = {
        [this.hashtagValueKey]: value,
      };

      this.$emit("update:modelValue", [...this.modelValue, formattedValue]);
      this.$emit("submit:hashtag", value);
      this.resetHoverOnButtons();
    },
    checkForDuplicates(value) {
      const hashTags = this.modelValue.map(item => this.getHashTagValue(item));
      return hashTags.includes(value);
    },
    formatHashtag(value) {
      let hash = value;
      if (this.splitHashTagsBy.includes(this.hashtag.split("").at(-1))) {
        hash = value.substring(0, this.hashtag.length - 1);
      }
      if (hash.startsWith("#")) {
        hash = hash.substring(1);
      }
      return hash.trim().toLowerCase();
    },
    handleKeydown(event) {
      if (this.keyboardKeysToSubmit.includes(event.key)) {
        this.updateHashtag(this.formatHashtag(this.hashtag));

        if (!this.keyboardKeysToContinueEditAfterSubmit.includes(event.key)) {
          this.showAddHashtag = false;
        } else {
          this.clearHashtag();
        }
      }
    },
    handleHashtagInput(event) {
      this.hashtag = event;
      if (!this.hashtag) {
        this.validationMessage = "";
        return;
      }
      if (this.hashtag === this.placeholder) {
        this.validationMessage = "";
        this.hashtag = "";
        return;
      }
      if (this.validationMessage) {
        return;
      }
      if (this.splitHashTagsBy.includes(this.hashtag.split("").at(-1))) {
        this.updateHashtag(this.formatHashtag(this.hashtag));
        this.$nextTick(() => {
          this.clearHashtag();
        });
      }
    },
    runValidation(value) {
      if (!value && !this.hashtag) {
        this.validationMessage = "";
        return;
      }
      const validationResult = this.validation(value || this.hashtag);
      if (typeof validationResult === "string") {
        this.validationMessage = validationResult;
      } else {
        this.validationMessage = "";
      }
      if (!this.allowDuplicates) {
        if (this.checkForDuplicates(value || this.hashtag)) {
          this.validationMessage = "Duplicate hashtags are not allowed";
        }
      }
    },
    clearHashtag() {
      this.hashtag = "";
      this.validationMessage = "";
      if (this.$refs.hashtagTextEdit) {
        this.$refs.hashtagTextEdit.innerText = "";
      }
      this.resetHoverOnButtons();
    },
    removeHashtag(value) {
      const removedItem = this.modelValue.find(
        item => this.getHashTagValue(item) === value,
      );

      const hashtags = this.modelValue.filter(
        item => this.getHashTagValue(item) !== value,
      );

      this.$emit("update:modelValue", hashtags);

      this.$emit("delete:hashtag", {
        el: removedItem,
      });

      this.resetHoverOnButtons();
    },
    getHashTagValue(item) {
      if (typeof item === "string") {
        return item;
      }
      return item[this.hashtagValueKey];
    },
    handleClickOutside() {
      if (this.showAddHashtag) {
        if (this.hashtag) {
          this.updateHashtag(this.formatHashtag(this.hashtag));
        }
      }
      this.showAddHashtag = false;
      this.resetHoverOnButtons();
    },
    saveTag() {
      this.handleClickOutside();
      this.resetHoverOnButtons();
    },
    closeAddTag() {
      this.hashtag = "";
      this.showAddHashtag = false;
      this.resetHoverOnButtons();
    },
    getTagFontColor(value) {
      return this.hoveredTagValue === value ? "light" : "standard";
    },
    resetHoverOnButtons() {
      this.addTagHovered = false;
      this.saveButtonHovered = false;
      this.closeButtonHovered = false;
      this.hoveredTagValue = null;
    },
  },
  styleGuide: () => ({
    editTextSpacingBlock: { "size.spacing": "standard" },
    addHashtagButtonBackgroundColor: { "color.background": "subtle" },
    addHashtagHoverFontColor: { "color.font": "standard" },
  }),
};
</script>

<style lang="scss" scoped>
.parent-input-box {
  position: relative;
}

.floating-input-box {
  top: -5px;
  position: absolute;
  z-index: 1;
}

:deep(.add-hashtag-btn:hover .i-text) {
  color: v-bind("$getStyles.addHashtagHoverFontColor") !important;
}

:deep(.add-hashtag-btn:hover svg) {
  fill: v-bind("$getStyles.addHashtagHoverFontColor") !important;
}
</style>
